import { useState, useEffect } from "react";
import { collection } from "firebase/firestore";
import Icon from "../ui/Icon";
import MiniSpinner from "../ui/MiniSpinner";
import { BRAND, THEMES, STAFF, STAFF_COLORS, STATUS_FLOW, STATUS_META } from "../../config/constants";
import useMobile from "../../hooks/useMobile";
import { qbCall } from "../../utils/api";
import { btnS } from "../../utils/styles";

export default function ReportsPage({ theme, clients }) {
  const t = THEMES[theme];
  const mobile = useMobile();

  const [qbData, setQbData]       = useState(null);
  const [qbLoading, setQbLoading] = useState(true);
  const [qbError, setQbError]     = useState("");

  const loadQB = () => {
    setQbLoading(true); setQbError("");
    qbCall("qbGetReports")
      .then(d => { setQbData(d); setQbLoading(false); })
      .catch(e => { setQbError(e.message); setQbLoading(false); });
  };
  useEffect(() => { loadQB(); }, []);

  const totalClients = clients.length;
  const byStatus     = STATUS_FLOW.reduce((a,s) => { a[s]=clients.filter(c=>c.status===s).length; return a; }, {});
  const byStaff      = STAFF.reduce((a,s) => { a[s]=clients.filter(c=>c.assignedTo===s).length; return a; }, {});
  const byType       = { Individual: clients.filter(c=>c.type==="Individual").length, Business: clients.filter(c=>c.type==="Business").length };
  const signed       = clients.filter(c=>c.engagementSigned).length;
  const completed    = byStatus["Completed"]||0;

  const fmt = (n) => "$" + (n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2});
  const pct = (a,b) => b ? Math.round(a/b*100) : 0;

  const BarChart = ({ data, height=100, color }) => {
    const max = Math.max(...data.map(d=>d.value), 1);
    return (
      <div style={{ display:"flex", alignItems:"flex-end", gap:3, height }}>
        {data.map((d,i) => (
          <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", height:"100%" }}>
            <div style={{ flex:1, width:"100%", display:"flex", alignItems:"flex-end" }}>
              <div title={d.label+": "+fmt(d.value)}
                style={{ width:"100%", borderRadius:"3px 3px 0 0",
                  background: d.value>0 ? color : t.border,
                  height: max>0 ? Math.max(3,(d.value/max)*100)+"%" : "3px",
                  transition:"height 0.5s ease" }}/>
            </div>
            <div style={{ fontSize:9, color:t.textMuted, marginTop:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", maxWidth:"100%" }}>{d.label}</div>
          </div>
        ))}
      </div>
    );
  };

  const Card = ({label,value,sub,color,icon,change}) => (
    <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:12,padding:"14px 16px",flex:1,minWidth:0}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div style={{flex:1}}>
          <div style={{fontSize:10,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:5}}>{label}</div>
          <div style={{fontSize:22,fontWeight:800,color:color||t.text,letterSpacing:"-0.5px"}}>{value}</div>
          {sub&&<div style={{fontSize:11,color:t.textMuted,marginTop:3}}>{sub}</div>}
          {change!==undefined&&<div style={{fontSize:11,fontWeight:600,color:change>=0?BRAND.green:"#f87171",marginTop:3}}>{change>=0?"↑":"↓"} {Math.abs(change)}% vs last year</div>}
        </div>
        {icon&&<div style={{fontSize:18,opacity:0.6}}>{icon}</div>}
      </div>
    </div>
  );

  const Bar = ({label,value,total,color}) => {
    const p = pct(value,total);
    return (
      <div style={{marginBottom:9}}>
        <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
          <span style={{fontSize:12,color:t.text,fontWeight:500}}>{label}</span>
          <span style={{fontSize:12,color:t.textMuted}}>{value} ({p}%)</span>
        </div>
        <div style={{height:6,borderRadius:3,background:t.border,overflow:"hidden"}}>
          <div style={{height:"100%",borderRadius:3,background:color,width:p+"%",transition:"width 0.6s"}}/>
        </div>
      </div>
    );
  };

  const Section = ({title,children,right}) => (
    <div style={{background:t.surface,border:`1px solid ${t.border}`,borderRadius:16,padding:22,marginBottom:18}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16,paddingBottom:10,borderBottom:`1px solid ${t.border}`}}>
        <div style={{fontSize:13,fontWeight:700,color:t.text}}>{title}</div>
        {right}
      </div>
      {children}
    </div>
  );

  const qbInv = qbData?.invoiceSummary || {};
  const yoy = qbInv.prevBilled > 0 ? pct((qbInv.totalBilled||0)-(qbInv.prevBilled||0), qbInv.prevBilled) : undefined;

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:20}}>
        <div>
          <div style={{fontSize:20,fontWeight:700,color:t.text,marginBottom:3}}>Dashboard</div>
          <div style={{fontSize:12.5,color:t.textMuted}}>Financial overview and client pipeline · {new Date().getFullYear()}</div>
        </div>
        <button onClick={loadQB} disabled={qbLoading} style={{...btnS(t),display:"flex",alignItems:"center",gap:5,fontSize:12,padding:"6px 14px"}}>
          <Icon name="refresh" size={12} color={BRAND.blue}/>{qbLoading?"Refreshing...":"Refresh QB"}
        </button>
      </div>

      {/* QB Finance — rich dashboard */}
      <Section title="📊 QuickBooks Finance — Revenue & Collections"
        right={<div style={{display:"flex",alignItems:"center",gap:10}}>
          <span style={{fontSize:11,color:t.textMuted}}>{qbData?.period?.label||("Last 12 months")}</span>
        </div>}>
        {qbLoading ? (
          <div style={{display:"flex",alignItems:"center",gap:10,color:t.textMuted,padding:24,justifyContent:"center"}}>
            <MiniSpinner size={18} color={BRAND.blue}/> Loading QuickBooks data...
          </div>
        ) : qbError ? (
          <div style={{background:theme==="dark"?"#1a0f00":"#fffbeb",border:"1px solid #f59e0b40",borderRadius:10,padding:"16px 20px"}}>
            <div style={{fontSize:13,fontWeight:600,color:"#f59e0b",marginBottom:4}}>⚠ QuickBooks not connected</div>
            <div style={{fontSize:12,color:t.textMuted,marginBottom:4}}>{qbError}</div>
            <div style={{fontSize:12,color:t.textMuted}}>Go to the <strong>QB page</strong> and reconnect.</div>
          </div>
        ) : qbData ? (<>

          {/* Top stat cards row */}
          <div style={{display:"grid",gridTemplateColumns:mobile?"1fr 1fr":"repeat(4,1fr)",gap:12,marginBottom:24}}>
            {[
              {label:"Total Revenue",    value:fmt(qbInv.totalBilled),  sub:yoy?(yoy>0?"↑":"↓")+" "+Math.abs(yoy)+"% vs last year":undefined, color:t.text,    bg:"transparent"},
              {label:"Collected",        value:fmt(qbInv.totalPaid),    sub:pct(qbInv.totalPaid,qbInv.totalBilled)+"% collection rate",        color:BRAND.green,bg:BRAND.green+"08"},
              {label:"Outstanding",      value:fmt(qbInv.totalBalance), sub:(qbInv.overdue||0)+" invoices overdue",                             color:"#f87171", bg:"#f8717108"},
              {label:"Active Customers", value:qbInv.totalCustomers||0, sub:(qbInv.count||0)+" invoices this year",                            color:BRAND.blue, bg:BRAND.blue+"08"},
            ].map(c=>(
              <div key={c.label} style={{background:c.bg||t.bg,border:`1px solid ${t.border}`,borderRadius:12,padding:"16px 18px"}}>
                <div style={{fontSize:10.5,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8,fontWeight:700}}>{c.label}</div>
                <div style={{fontSize:22,fontWeight:800,color:c.color,letterSpacing:"-0.5px",marginBottom:4}}>{c.value}</div>
                {c.sub&&<div style={{fontSize:11,color:t.textMuted}}>{c.sub}</div>}
              </div>
            ))}
          </div>

          {/* Bar chart + donut row */}
          <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"3fr 2fr",gap:20,marginBottom:24}}>
            {/* Monthly revenue bar chart */}
            <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:14,padding:"18px 20px"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                <div style={{fontSize:12,fontWeight:700,color:t.text,textTransform:"uppercase",letterSpacing:"0.07em"}}>Revenue — Last 12 Months</div>
                <div style={{fontSize:11,color:t.textMuted}}>Total: <strong style={{color:t.text}}>{fmt(qbInv.totalBilled)}</strong></div>
              </div>
              {(()=>{
                const months = qbData.monthlyRevenue||[];
                const maxVal = Math.max(...months.map(m=>m.revenue),1);
                const hasData = months.some(m=>m.revenue>0);
                if(!hasData) return <div style={{textAlign:"center",padding:"30px 0",color:t.textMuted,fontSize:12,fontStyle:"italic"}}>No invoice data in the last 12 months</div>;
                return(
                  <div style={{display:"flex",alignItems:"flex-end",gap:4,height:130}}>
                    {months.map((m,i)=>{
                      const h = Math.max((m.revenue/maxVal)*110, m.revenue>0?4:0);
                      const isHighest = m.revenue===maxVal && m.revenue>0;
                      return(
                        <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:4}}>
                          <div style={{fontSize:9,color:t.textMuted,fontWeight:600,height:16,display:"flex",alignItems:"center"}}>
                            {m.revenue>0?("$"+(m.revenue>=1000?(m.revenue/1000).toFixed(0)+"k":m.revenue.toFixed(0))):""}
                          </div>
                          <div title={m.label+": "+fmt(m.revenue)}
                            style={{width:"100%",height:h,borderRadius:"4px 4px 0 0",
                              background:isHighest?BRAND.green:(m.revenue>0?BRAND.blue:t.border+"60"),
                              transition:"height 0.4s ease",cursor:"default",minHeight:m.revenue>0?4:0}}>
                          </div>
                          <div style={{fontSize:9,color:t.textMuted,fontWeight:600}}>{m.label}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Collection split donut-style */}
            <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:14,padding:"18px 20px",display:"flex",flexDirection:"column"}}>
              <div style={{fontSize:12,fontWeight:700,color:t.text,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:16}}>Collection Split</div>
              {/* SVG donut */}
              {(()=>{
                const total = qbInv.totalBilled||1;
                const paid  = qbInv.totalPaid||0;
                const owed  = qbInv.totalBalance||0;
                const overdue = Math.min(owed * 0.4, owed); // approximate
                const r=52, cx=75, cy=75, circ=2*Math.PI*r;
                const paidPct   = paid/total;
                const owedPct   = (owed-overdue)/total;
                const overduePct= overdue/total;
                const segments=[
                  {color:BRAND.green,  pct:paidPct,    label:"Collected",  val:fmt(paid)},
                  {color:"#f87171",    pct:overduePct, label:"Overdue est.",val:fmt(overdue)},
                  {color:"#fbbf24",    pct:owedPct,    label:"Pending",     val:fmt(owed-overdue)},
                ];
                let offset=0;
                return(
                  <div style={{display:"flex",alignItems:"center",gap:16,flex:1}}>
                    <svg width="150" height="150" viewBox="0 0 150 150">
                      <circle cx={cx} cy={cy} r={r} fill="none" stroke={t.border} strokeWidth="20"/>
                      {paid>0&&segments.map((seg,i)=>{
                        const dash=circ*seg.pct;
                        const gap=circ*(1-seg.pct);
                        const el=<circle key={i} cx={cx} cy={cy} r={r} fill="none"
                          stroke={seg.color} strokeWidth="20"
                          strokeDasharray={`${dash} ${gap}`}
                          strokeDashoffset={-offset*circ}
                          style={{transform:"rotate(-90deg)",transformOrigin:"75px 75px",transition:"stroke-dasharray 0.6s"}}
                        />;
                        offset+=seg.pct;
                        return el;
                      })}
                      <text x={cx} y={cy-6} textAnchor="middle" style={{fontSize:11,fontWeight:800,fill:t.text,fontFamily:"Outfit"}}>{fmt(paid)}</text>
                      <text x={cx} y={cy+10} textAnchor="middle" style={{fontSize:9,fill:t.textMuted,fontFamily:"Outfit"}}>collected</text>
                    </svg>
                    <div style={{flex:1,display:"flex",flexDirection:"column",gap:10}}>
                      {segments.map((seg,i)=>(
                        <div key={i} style={{display:"flex",alignItems:"center",gap:7}}>
                          <div style={{width:10,height:10,borderRadius:3,background:seg.color,flexShrink:0}}/>
                          <div style={{flex:1}}>
                            <div style={{fontSize:11,color:t.textMuted}}>{seg.label}</div>
                            <div style={{fontSize:12.5,fontWeight:700,color:t.text}}>{seg.val}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Bottom row — Invoice status + Top clients */}
          <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr",gap:20}}>
            {/* Invoice status bars */}
            <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:14,padding:"18px 20px"}}>
              <div style={{fontSize:12,fontWeight:700,color:t.text,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:14}}>Invoice Status</div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:16}}>
                {[
                  {label:"Total",   val:qbInv.count||0,        color:t.text},
                  {label:"Paid",    val:qbInv.paidCount||0,    color:BRAND.green},
                  {label:"Unpaid",  val:qbInv.unpaidCount||0,  color:"#fbbf24"},
                  {label:"Overdue", val:qbInv.overdue||0,      color:"#f87171"},
                ].map(s=>(
                  <div key={s.label} style={{textAlign:"center",padding:"10px 8px",borderRadius:10,background:s.color+"10",border:`1px solid ${s.color}20`}}>
                    <div style={{fontSize:20,fontWeight:800,color:s.color}}>{s.val}</div>
                    <div style={{fontSize:10.5,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.06em"}}>{s.label}</div>
                  </div>
                ))}
              </div>
              <div style={{height:8,borderRadius:4,overflow:"hidden",background:t.border,display:"flex"}}>
                <div style={{height:"100%",background:BRAND.green,width:pct(qbInv.paidCount||0,qbInv.count||1)+"%",transition:"width 0.6s"}}/>
                <div style={{height:"100%",background:"#fbbf24",width:pct((qbInv.unpaidCount||0)-(qbInv.overdue||0),qbInv.count||1)+"%"}}/>
                <div style={{height:"100%",background:"#f87171",flex:1}}/>
              </div>
              <div style={{display:"flex",gap:12,marginTop:6}}>
                {[{c:BRAND.green,l:"Paid"},{c:"#fbbf24",l:"Pending"},{c:"#f87171",l:"Overdue"}].map(x=>(
                  <div key={x.l} style={{display:"flex",alignItems:"center",gap:4,fontSize:10.5,color:t.textMuted}}>
                    <div style={{width:8,height:8,borderRadius:2,background:x.c}}/>
                    {x.l}
                  </div>
                ))}
              </div>
            </div>

            {/* Top clients */}
            <div style={{background:t.bg,border:`1px solid ${t.border}`,borderRadius:14,padding:"18px 20px"}}>
              <div style={{fontSize:12,fontWeight:700,color:t.text,textTransform:"uppercase",letterSpacing:"0.07em",marginBottom:14}}>Top Clients by Revenue</div>
              {(qbData.topCustomers||[]).length===0
                ? <div style={{fontSize:12,color:t.textMuted,fontStyle:"italic",padding:"20px 0",textAlign:"center"}}>No data yet</div>
                : (qbData.topCustomers||[]).map((c,i)=>{
                    const maxC = qbData.topCustomers[0]?.total||1;
                    return(
                      <div key={i} style={{marginBottom:12}}>
                        <div style={{display:"flex",justifyContent:"space-between",marginBottom:4}}>
                          <div style={{display:"flex",alignItems:"center",gap:6}}>
                            <span style={{fontSize:10,color:"#fff",fontWeight:800,background:[BRAND.blue,BRAND.green,"#f59e0b","#8b5cf6","#06b6d4"][i]||BRAND.blue,width:18,height:18,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>{i+1}</span>
                            <span style={{fontSize:12.5,color:t.text,fontWeight:500,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{c.name}</span>
                          </div>
                          <span style={{fontSize:12.5,fontWeight:700,color:BRAND.green,flexShrink:0}}>{fmt(c.total)}</span>
                        </div>
                        <div style={{height:5,borderRadius:3,background:t.border,overflow:"hidden"}}>
                          <div style={{height:"100%",borderRadius:3,background:[BRAND.blue,BRAND.green,"#f59e0b","#8b5cf6","#06b6d4"][i]||BRAND.blue,width:(c.total/maxC*100)+"%",transition:"width 0.5s"}}/>
                        </div>
                      </div>
                    );
                  })
              }
            </div>
          </div>
        </>) : null}
      </Section>

      {/* Client Pipeline */}
      <Section title="📋 Client Pipeline">
        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:20}}>
          <Card label="Total Clients"      value={totalClients}                           icon="👥" color={BRAND.blue}/>
          <Card label="Completed"          value={completed}                              icon="✅" color={BRAND.green} sub={pct(completed,totalClients)+"% done"}/>
          <Card label="Signed"             value={signed}                                 icon="✍"  color={BRAND.blue}  sub={pct(signed,totalClients)+"% signed"}/>
          <Card label="In Progress"        value={byStatus["Tax Return in Progress"]||0}  icon="⚙"  color="#f59e0b"/>
          <Card label="Not Started"        value={byStatus["Not Started"]||0}             icon="○"  color={t.textMuted}/>
        </div>
        <div style={{display:"grid",gridTemplateColumns:mobile?"1fr":"1fr 1fr",gap:20}}>
          <div>
            <div style={{fontSize:11,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:10}}>By Status</div>
            {STATUS_FLOW.map(s=><Bar key={s} label={s} value={byStatus[s]||0} total={totalClients} color={STATUS_META[s].text}/>)}
            <div style={{marginTop:14}}>
              <div style={{fontSize:11,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:8}}>Pipeline Chart</div>
              <BarChart data={STATUS_FLOW.map(s=>({label:s.split(" ")[0],value:byStatus[s]||0}))} color={BRAND.blue} height={80}/>
            </div>
          </div>
          <div>
            <div style={{fontSize:11,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:10}}>By Staff</div>
            {STAFF.map(s=><Bar key={s} label={s} value={byStaff[s]||0} total={totalClients} color={STAFF_COLORS[s]}/>)}
            <div style={{marginTop:14}}>
              <div style={{fontSize:11,color:t.textMuted,textTransform:"uppercase",letterSpacing:"0.07em",fontWeight:700,marginBottom:10}}>By Client Type</div>
              <Bar label="Individual" value={byType.Individual} total={totalClients} color={BRAND.blue}/>
              <Bar label="Business"   value={byType.Business}   total={totalClients} color={BRAND.green}/>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ─── Import Page ─────────────────────────────────────────────────────────────
